/**
 * Safe expression evaluator for pack conditions.
 *
 * Packs are untrusted (third-party uploads), so `eval` / `new Function` are off
 * the table — a malicious pack would get arbitrary code execution in the player's
 * browser and, worse, inside the editor. This is a small recursive-descent parser
 * over a deliberately tiny grammar instead.
 *
 *   expr    := or
 *   or      := and ( '||' and )*
 *   and     := not ( '&&' not )*
 *   not     := '!' not | cmp
 *   cmp     := atom ( ('=='|'!='|'>='|'<='|'>'|'<') atom )?
 *   atom    := number | string | bool | path | '(' expr ')'
 *   path    := ident ( '.' ident )*
 */

export type Scope = Record<string, unknown>;

type Tok =
  | { t: 'num'; v: number }
  | { t: 'str'; v: string }
  | { t: 'bool'; v: boolean }
  | { t: 'id'; v: string }
  | { t: 'op'; v: string };

const OPS = ['&&', '||', '==', '!=', '>=', '<=', '!', '>', '<', '(', ')', '.'];

function lex(src: string): Tok[] {
  const out: Tok[] = [];
  let i = 0;
  while (i < src.length) {
    const c = src[i]!;
    if (/\s/.test(c)) { i++; continue; }

    if (/[0-9]/.test(c)) {
      let j = i;
      while (j < src.length && /[0-9.]/.test(src[j]!)) j++;
      out.push({ t: 'num', v: Number(src.slice(i, j)) });
      i = j;
      continue;
    }

    if (c === '"' || c === "'") {
      let j = i + 1;
      let s = '';
      while (j < src.length && src[j] !== c) {
        // no escape sequences — keeps the lexer trivially correct
        s += src[j];
        j++;
      }
      if (j >= src.length) throw new SyntaxError(`Unterminated string in: ${src}`);
      out.push({ t: 'str', v: s });
      i = j + 1;
      continue;
    }

    if (/[A-Za-z_]/.test(c)) {
      let j = i;
      while (j < src.length && /[A-Za-z0-9_]/.test(src[j]!)) j++;
      const word = src.slice(i, j);
      if (word === 'true' || word === 'false') out.push({ t: 'bool', v: word === 'true' });
      else out.push({ t: 'id', v: word });
      i = j;
      continue;
    }

    const op = OPS.find((o) => src.startsWith(o, i));
    if (!op) throw new SyntaxError(`Unexpected character '${c}' in: ${src}`);
    out.push({ t: 'op', v: op });
    i += op.length;
  }
  return out;
}

class Parser {
  private pos = 0;
  constructor(private readonly toks: Tok[], private readonly scope: Scope) {}

  private peek(): Tok | undefined { return this.toks[this.pos]; }

  private eat(v: string): boolean {
    const t = this.peek();
    if (t?.t === 'op' && t.v === v) { this.pos++; return true; }
    return false;
  }

  parse(): unknown {
    const v = this.or();
    if (this.pos !== this.toks.length) throw new SyntaxError('Trailing tokens in expression');
    return v;
  }

  private or(): unknown {
    let left = this.and();
    while (this.eat('||')) {
      const right = this.and();
      left = truthy(left) || truthy(right);
    }
    return left;
  }

  private and(): unknown {
    let left = this.not();
    while (this.eat('&&')) {
      const right = this.not();
      left = truthy(left) && truthy(right);
    }
    return left;
  }

  private not(): unknown {
    if (this.eat('!')) return !truthy(this.not());
    return this.cmp();
  }

  private cmp(): unknown {
    const left = this.atom();
    for (const op of ['==', '!=', '>=', '<=', '>', '<'] as const) {
      if (this.eat(op)) {
        const right = this.atom();

        // Chained comparison. `a > b > c` parses as `(a > b) > c`, comparing a
        // boolean against a number and silently yielding false — so reject it
        // rather than let an author ship a condition that can never be true.
        const next = this.peek();
        if (next?.t === 'op' && ['==', '!=', '>=', '<=', '>', '<'].includes(next.v)) {
          throw new SyntaxError(`Chained comparison "${op} ... ${next.v}" is not supported; use && instead`);
        }

        if (op === '==') return left === right;
        if (op === '!=') return left !== right;

        /*
         * Ordering comparisons require numbers on both sides.
         *
         * Previously these coerced via Number(), while == and != used strict
         * ===. That made `set x = 1` behave inconsistently: `x == "1"` was
         * false but `x >= "1"` was true. Rather than pick one coercion rule and
         * hope authors internalise it, ordering is now numbers-only and anything
         * else is an authoring error the evaluator reports.
         */
        if (typeof left !== 'number' || typeof right !== 'number') {
          throw new SyntaxError(
            `Ordering comparison "${op}" needs numbers on both sides, got ` +
            `${typeof left} and ${typeof right}. Compare with == for text.`,
          );
        }

        switch (op) {
          case '>=': return left >= right;
          case '<=': return left <= right;
          case '>': return left > right;
          case '<': return left < right;
        }
      }
    }
    return left;
  }

  private atom(): unknown {
    if (this.eat('(')) {
      const v = this.or();
      if (!this.eat(')')) throw new SyntaxError('Expected )');
      return v;
    }
    const t = this.peek();
    if (!t) throw new SyntaxError('Unexpected end of expression');
    this.pos++;

    if (t.t === 'num' || t.t === 'str' || t.t === 'bool') return t.v;
    if (t.t === 'id') {
      let cur: unknown = this.scope[t.v];
      while (this.eat('.')) {
        const key = this.peek();
        if (key?.t !== 'id') throw new SyntaxError('Expected identifier after .');
        this.pos++;
        // Reject prototype access outright rather than relying on it being undefined.
        if (key.v === '__proto__' || key.v === 'constructor' || key.v === 'prototype') {
          throw new SyntaxError(`Forbidden property access: ${key.v}`);
        }
        cur = cur == null ? undefined : (cur as Record<string, unknown>)[key.v];
      }
      return cur;
    }
    throw new SyntaxError(`Unexpected token ${JSON.stringify(t)}`);
  }
}

function truthy(v: unknown): boolean {
  return v !== undefined && v !== null && v !== false && v !== 0 && v !== '';
}

function num(v: unknown): number {
  return typeof v === 'number' ? v : Number(v);
}

/**
 * Evaluate a pack condition. Returns `false` on any parse/eval failure rather
 * than throwing — a broken condition in one line of a third-party pack should
 * hide that branch, not crash the player's session.
 */
export function evaluate(expr: string, scope: Scope): boolean {
  try {
    return truthy(new Parser(lex(expr), scope).parse());
  } catch (err) {
    console.warn(`[hon] bad condition ${JSON.stringify(expr)}:`, err);
    return false;
  }
}

/** Strict variant for the editor — surfaces syntax errors to pack authors. */
export function evaluateStrict(expr: string, scope: Scope): boolean {
  return truthy(new Parser(lex(expr), scope).parse());
}

/**
 * Every dotted path a condition references, without evaluating it.
 *
 * The validator needs this to answer "does this condition name a flag nobody
 * ever writes?" — the check that turns a typo from a silently-hidden branch
 * (see `evaluate`'s catch) into a build failure. `evaluate` and
 * `evaluateStrict` both return a boolean and throw the path list away, so this
 * is the only way to get at it.
 *
 * It walks the token stream rather than the parser, deliberately. `path` is the
 * one production that is unambiguous at the token level — an `id` followed by
 * any number of `.id` pairs — so extraction cannot be thrown off by operator
 * precedence, and it cannot miss a name inside a branch that a future
 * short-circuiting evaluator would skip.
 *
 * Grammar is NOT checked here: `s_x &&` extracts `['s_x']` quite happily.
 * Syntax is `evaluateStrict`'s job (validator rule 10). Lexical errors — a bad
 * character, an unterminated string — still throw, because there is no token
 * stream to walk.
 *
 * @returns unique paths in first-seen order, e.g. `['s_police', 'vars.k_x']`
 */
export function extractPaths(expr: string): string[] {
  const toks = lex(expr);
  const out: string[] = [];
  const seen = new Set<string>();

  for (let i = 0; i < toks.length; i++) {
    const t = toks[i]!;
    if (t.t !== 'id') continue;

    // A `.` only continues a path when an identifier actually follows it;
    // otherwise it is a syntax error for evaluateStrict to report, not ours.
    let path = t.v;
    while (toks[i + 1]?.t === 'op' && (toks[i + 1] as { v: string }).v === '.' && toks[i + 2]?.t === 'id') {
      path += `.${(toks[i + 2] as { v: string }).v}`;
      i += 2;
    }

    if (!seen.has(path)) { seen.add(path); out.push(path); }
  }
  return out;
}

/** The first segment of a dotted path — what the registry and the reserved-name check key on. */
export function rootOf(path: string): string {
  return path.split('.', 1)[0]!;
}
